import React, { useState, useRef, useEffect } from 'react';
import { Download, Sparkles, FileText, ArrowRight, Lightbulb, BookOpen, Code, Plus, Minus, Upload, Save, RefreshCw, Maximize2, X, ExternalLink, Trash2, Link2, GripVertical } from 'lucide-react';
import { useAssessment } from './SharedContext';
import { generateAssignmentHeader, hasSchoolLogo } from './logoIntegrationUtility';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';

// TipTap MenuBar (from RubricCreator.js)
const MenuBar = ({ editor }) => {
    if (!editor) {
        return null;
    }

    const setLink = () => {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    const addImage = () => {
        const url = window.prompt('Image URL');
        if (url) {
            editor.chain().focus().setImage({ src: url }).run();
        }
    };

    return (
        <div className="p-2 border-b border-gray-300 bg-gray-50 flex items-center flex-wrap gap-x-2 gap-y-1 text-sm">
            <button title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>Undo</button>
            <button title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>Redo</button>
            <div className="w-[1px] h-5 bg-gray-300 mx-1" />
            <button onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''} title="Bold"><b>B</b></button>
            <button onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''} title="Italic"><i>I</i></button>
            <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor.isActive('underline') ? 'is-active' : ''} title="Underline"><u>U</u></button>
            <button onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active' : ''} title="Strikethrough"><s>S</s></button>
            <div className="w-[1px] h-5 bg-gray-300 mx-1" />
            <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''} title="Heading 1">H1</button>
            <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''} title="Heading 2">H2</button>
            <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''} title="Heading 3">H3</button>
            <div className="w-[1px] h-5 bg-gray-300 mx-1" />
            <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''} title="Bullet List">• List</button>
            <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'is-active' : ''} title="Numbered List">1. List</button>
            <div className="w-[1px] h-5 bg-gray-300 mx-1" />
            <button onClick={() => editor.chain().focus().setTextAlign('left').run()} className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''} title="Align Left">Left</button>
            <button onClick={() => editor.chain().focus().setTextAlign('center').run()} className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''} title="Align Center">Center</button>
            <button onClick={() => editor.chain().focus().setTextAlign('right').run()} className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''} title="Align Right">Right</button>
            <div className="w-[1px] h-5 bg-gray-300 mx-1" />
            <button onClick={setLink} className={editor.isActive('link') ? 'is-active' : ''} title="Add Link">Link</button>
            <button onClick={addImage} title="Add Image">Image</button>
            <style jsx>{`
                button { padding: 0.25rem 0.5rem; border-radius: 4px; transition: background-color 0.2s; }
                button:hover { background-color: #e5e7eb; }
                button.is-active { background-color: #dbeafe; color: #1d4ed8; font-weight: bold; }
                button:disabled { opacity: 0.5; cursor: not-allowed; }
            `}</style>
        </div>
    );
};

// TiptapEditor component (from RubricCreator.js)
const TiptapEditor = React.forwardRef(({ value, onChange, placeholder, showToolbar = true, editorClassName = 'min-h-[200px]' }, ref) => {
    const editor = useEditor({
        extensions: [StarterKit, Underline, Image, Link.configure({ openOnClick: false }), TextAlign.configure({ types: ['heading', 'paragraph'] })],
        content: value || '',
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: `${editorClassName} p-4 focus:outline-none`,
            },
        },
    });

    React.useImperativeHandle(ref, () => editor, [editor]);

    useEffect(() => {
        if (editor && !editor.isDestroyed) {
            const isSame = editor.getHTML() === value;
            if (!isSame) {
                editor.commands.setContent(value, false);
            }
        }
    }, [value, editor]);

    return (
        <div className="rich-text-editor bg-white border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all duration-200">
            {showToolbar && <MenuBar editor={editor} />}
            <EditorContent editor={editor} placeholder={placeholder} />
            <style>{`
                .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); float: left; color: #adb5bd; pointer-events: none; height: 0; }
                .ProseMirror h1 { font-size: 1.75em; font-weight: bold; }
                .ProseMirror h2 { font-size: 1.5em; font-weight: bold; }
                .ProseMirror h3 { font-size: 1.25em; font-weight: bold; }
                .ProseMirror ul { list-style-type: disc; padding-left: 2rem; }
                .ProseMirror ol { list-style-type: decimal; padding-left: 2rem; }
                .ProseMirror img { max-width: 100%; height: auto; display: block; margin-left: auto; margin-right: auto; }
                .ProseMirror a { color: #2563eb; text-decoration: underline; cursor: pointer; }
            `}</style>
        </div>
    );
});
TiptapEditor.displayName = 'TiptapEditor';


const AssignmentPromptGenerator = () => {
    const {
        assignmentPromptFormData,
        updateAssignmentPromptFormData,
        initializeAssignmentPromptFormData
    } = useAssessment();

    useEffect(() => {
        if (!assignmentPromptFormData) {
            initializeAssignmentPromptFormData();
        }
    }, [assignmentPromptFormData, initializeAssignmentPromptFormData]);

    const [generatedPrompt, setGeneratedPrompt] = useState('');
    const [showPrompt, setShowPrompt] = useState(false);
    const [generatedHTML, setGeneratedHTML] = useState('');
    const [showHTML, setShowHTML] = useState(false);
    const resultsRef = useRef(null);
    const fileInputRef = useRef(null);
    const [inlineEditor, setInlineEditor] = useState({ show: false, content: '', field: null, onSave: null });
    const richTextContentRef = useRef('');
    const editorRef = useRef(null);

    useEffect(() => {
        if ((showPrompt || showHTML) && resultsRef.current) {
            resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [showPrompt, showHTML]);

    const handleInputChange = (field, value) => {
        updateAssignmentPromptFormData(field, value);
        if (field === 'assignmentNumber' && value) {
            updateAssignmentPromptFormData('submissionFolderNumber', value);
        }
    };

    const saveAssignmentJSON = () => {
        if (!assignmentPromptFormData) {
            alert('No assignment data to save');
            return;
        }
        const dataToSave = { ...assignmentPromptFormData, savedDate: new Date().toISOString(), version: '1.0' };
        const dataStr = JSON.stringify(dataToSave, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        const assignmentTitle = assignmentPromptFormData.assignmentTitle || 'Assignment';
        const assignmentNumber = assignmentPromptFormData.assignmentNumber || 'X';
        const courseCode = assignmentPromptFormData.courseCode || 'Course';
        link.download = `${courseCode}_Assignment_${assignmentNumber}_${assignmentTitle.replace(/\s+/g, '_')}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        alert('Assignment saved successfully!');
    };

    const loadAssignmentJSON = () => {
        fileInputRef.current?.click();
    };

    const handleFileLoad = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const jsonData = JSON.parse(e.target.result);
                if (typeof jsonData !== 'object' || !jsonData.assignmentTitle) {
                    throw new Error('Invalid assignment file format');
                }
                const { savedDate, version, ...assignmentData } = jsonData;
                Object.entries(assignmentData).forEach(([field, value]) => {
                    updateAssignmentPromptFormData(field, value);
                });
                alert(`Assignment loaded successfully!${savedDate ? `\nSaved: ${new Date(savedDate).toLocaleDateString()}` : ''}`);
            } catch (error) {
                console.error('Error loading assignment:', error);
                alert('Error loading assignment file. Please ensure it\'s a valid assignment JSON file.');
            }
        };
        reader.readAsText(file);
        event.target.value = '';
    };

    const openInlineEditor = (content, field, onSave) => {
        richTextContentRef.current = content || '';
        setInlineEditor({ show: true, content: content || '', field, onSave });
    };

    const closeInlineEditor = (save = false) => {
        if (save && inlineEditor.onSave) {
            inlineEditor.onSave(richTextContentRef.current);
        }
        setInlineEditor({ show: false, content: '', field: null, onSave: null });
        richTextContentRef.current = '';
    };

    const renderFormattedContent = (htmlContent) => {
        if (!htmlContent) return '';
        let textContent = htmlContent
            .replace(/\r\n/g, '\n')
            .replace(/<p><br\s*\/?><\/p>/gi, '\n\n')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/p>/gi, '\n')
            .replace(/<p[^>]*>/gi, '')
            .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
            .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
            .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
            .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
            .replace(/<u[^>]*>(.*?)<\/u>/gi, '_$1_')
            .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n')
            .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n')
            .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n')
            .replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n')
            .replace(/<\/(ul|ol)>/gi, '\n')
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .trim();
        return textContent;
    };


    const addCLO = () => {
        const currentCLOs = formData.clos || [];
        if (currentCLOs.length < 15) {
            const newCLO = { id: Date.now(), number: (currentCLOs.length + 1).toString(), text: '', type: 'CLO' };
            handleInputChange('clos', [...currentCLOs, newCLO]);
        }
    };

    const removeCLO = (cloId) => {
        const currentCLOs = formData.clos || [];
        if (currentCLOs.length > 1) {
            handleInputChange('clos', currentCLOs.filter(clo => clo.id !== cloId));
        }
    };

    const updateCLO = (cloId, field, value) => {
        const currentCLOs = formData.clos || [];
        handleInputChange('clos', currentCLOs.map(clo => clo.id === cloId ? { ...clo, [field]: value } : clo));
    };

    const generateNumberOptions = () => {
        const options = [];
        for (let i = 1; i <= 15; i++) {
            options.push(<option key={i} value={i.toString()}>{i}</option>);
            for (let j = 1; j <= 5; j++) {
                const decimal = `${i}.${j}`;
                options.push(<option key={decimal} value={decimal}>{decimal}</option>);
            }
        }
        return options;
    };

    const generateWeekOptions = () => {
        const options = [<option key="default" value="">Select Week</option>];
        for (let i = 1; i <= 16; i++) {
            options.push(<option key={i} value={i.toString()}>{`Week ${i}`}</option>);
        }
        return options;
    };

    const HYPERLINK_CATEGORIES = [
        { value: 'handout', label: 'Handout' },
        { value: 'd2l', label: 'D2L Link' },
        { value: 'reference', label: 'Reference Material' },
        { value: 'tool', label: 'Tool / Software' },
        { value: 'video', label: 'Video / Tutorial' },
        { value: 'other', label: 'Other' }
    ];

    const addHyperlink = () => {
        const currentLinks = (assignmentPromptFormData?.hyperlinks) || [];
        const newLink = { id: Date.now(), label: '', url: '', category: 'handout' };
        handleInputChange('hyperlinks', [...currentLinks, newLink]);
    };

    const removeHyperlink = (linkId) => {
        const currentLinks = (assignmentPromptFormData?.hyperlinks) || [];
        handleInputChange('hyperlinks', currentLinks.filter(link => link.id !== linkId));
    };

    const updateHyperlink = (linkId, field, value) => {
        const currentLinks = (assignmentPromptFormData?.hyperlinks) || [];
        handleInputChange('hyperlinks', currentLinks.map(link => link.id === linkId ? { ...link, [field]: value } : link));
    };

    const formData = assignmentPromptFormData || {
        assignmentTitle: '', assignmentNumber: '1', weightPercentage: '', courseCode: '',
        programLevel: '', subjectArea: '', assignmentDescription: '',
        rationale: 'This assignment will evaluate the following Course Learning Outcomes:',
        clos: [{ id: 1, number: '1', text: '', type: 'CLO' }, { id: 2, number: '2', text: '', type: 'CLO' }, { id: 3, number: '3', text: '', type: 'CLO' }, { id: 4, number: '4', text: '', type: 'CLO' }],
        directions: '', gradingDetails: '', gradingCriteria: '', dueWeek: '', dueDateCustom: '',
        submissionFolderNumber: '1', specialInstructions: '', tipsForSuccess: '',
        hyperlinks: []
    };

    const generateAIPrompt = () => {
        const clos = formData.clos || [];
        const closText = clos.filter(clo => clo.text.trim() !== '').map(clo => `- ${clo.type}${clo.number}: ${clo.text}`).join('\n');
        const dueText = formData.dueWeek ? `Week ${formData.dueWeek}${formData.dueDateCustom ? ` (${formData.dueDateCustom})` : ''}` : formData.dueDateCustom || 'See your Instructional Plan for exact due dates.';
        const submissionFolderText = `Assignment ${formData.submissionFolderNumber || formData.assignmentNumber || 'X'}`;
        const prompt = `Create HTML code for an assignment details page that matches EXACTLY the structure and format of the provided template for D2L. Use the following assignment information:

**Assignment Information:**
- Assignment Title: Individual Assignment ${formData.assignmentNumber} (${formData.weightPercentage}%)
- Assignment Number: ${formData.assignmentNumber}
- Weight: ${formData.weightPercentage}%
- Course Code: ${formData.courseCode}
- Subject Area: ${formData.subjectArea}
- Due Date: ${dueText}
- Submission Folder: ${submissionFolderText}

**Assignment Content:**
- Description: ${renderFormattedContent(formData.assignmentDescription)}
- Rationale: ${renderFormattedContent(formData.rationale)}
- Learning Outcomes:
${closText}
- Directions: ${renderFormattedContent(formData.directions)}
- Grading Details: ${renderFormattedContent(formData.gradingDetails)}
- Grading Criteria: ${renderFormattedContent(formData.gradingCriteria)}
- Tips for Success: ${renderFormattedContent(formData.tipsForSuccess)}
- Special Instructions: ${renderFormattedContent(formData.specialInstructions)}
${(formData.hyperlinks || []).filter(l => l.label && l.url).length > 0 ? `
**Resource Links:**
${(formData.hyperlinks || []).filter(l => l.label && l.url).map(l => `- ${l.label}: ${l.url} (${HYPERLINK_CATEGORIES.find(c => c.value === l.category)?.label || l.category})`).join('\n')}
` : ''}
**CRITICAL REQUIREMENTS:**
1. Use EXACTLY this HTML structure for D2L content area (NO DOCTYPE, html, head, or body tags):

\`\`\`html
<div class="container-fluid">
<div class="row justify-content-center">
<div class="col-sm-12 col-md-10">
<p><strong>Due:</strong>&nbsp; [DUE DATE]</p>
<p><strong>Submit to: </strong>The&nbsp;<strong>[SUBMISSION FOLDER]</strong>&nbsp;submission folder. Submission folders can be accessed by selecting <strong>Course Tools</strong> and then <strong>Assignments </strong>on the course navigation bar.</p>
<h2>Description</h2>
[DESCRIPTION]
<h3>Rationale</h3>
[RATIONALE TEXT]
<ul>
[LEARNING OUTCOME LIST ITEMS]
</ul>
<h2>Directions</h2>
[DIRECTIONS]
<h2>How Your Assignment Will be Graded</h2>
[GRADING INFORMATION]
<h2>Grading Criteria</h2>
[GRADING CRITERIA]
<p>See your Program Handbook for the late policy.</p>
<h2>Tips for Success</h2>
[TIPS FOR SUCCESS]
</div>
</div>
</div>
\`\`\`

2. Replace all bracketed placeholders with the appropriate information provided above
3. This is for D2L content area - do NOT include DOCTYPE, html, head, or body tags
4. Follow the content structure exactly as shown in the simple template
5. Use proper HTML formatting with <strong>, <h2>, <h3>, <ul>, <li>, <p> tags
6. For the learning outcome list, create <li> tags with the format: <li>CLO#: [text]</li> or <li>ULO#: [text]</li>
7. For grading section: use the provided grading details and grading criteria text
8. Keep the format simple and clean - this is the basic template structure

Generate complete, ready-to-paste HTML code for D2L content area that matches the simple template structure exactly.`;
        setGeneratedPrompt(prompt);
        setShowPrompt(true);
        setShowHTML(false);
    };

    const generateAssignmentHTML = () => {
        const clos = formData.clos || [];
        const closHTML = clos.filter(clo => clo.text.trim() !== '').map(clo => `    <li><strong>${clo.type} ${clo.number}:</strong> ${clo.text}</li>`).join('\n');
        const dueText = formData.dueWeek ? `(Week ${formData.dueWeek}${formData.dueDateCustom ? ` - ${formData.dueDateCustom}` : ''})` : formData.dueDateCustom ? `(${formData.dueDateCustom})` : '(See your Instructional Plan for exact due dates)';
        const submissionFolderText = `Assignment ${formData.submissionFolderNumber || formData.assignmentNumber || 'X'}`;
        const formatRichTextForHTML = (htmlContent, fallbackText) => {
            if (!htmlContent || htmlContent.trim() === '') return fallbackText;
            return htmlContent.includes('<') && htmlContent.includes('>') ? htmlContent.replace(/<div>/g, '<p>').replace(/<\/div>/g, '</p>').replace(/<br\s*\/?>/g, '<br>').trim() : `<p>${htmlContent}</p>`;
        };
        const assignmentHTML = `<div class="container-fluid">
<div class="row justify-content-center">
<div class="col-sm-12 col-md-10">
  <h1>Assignment ${formData.assignmentNumber || 'X'}: ${formData.assignmentTitle || '[Assignment Title]'}</h1>
  <p><strong>Due:</strong> ${dueText}</p>
  <p><strong>Weight:</strong> ${formData.weightPercentage || 'X'}% of final grade</p>
  <p><strong>Submit to: </strong>The <strong>${submissionFolderText}</strong> submission folder. Submission folders can be accessed by selecting <strong>Course Tools</strong> and then <strong>Assignments</strong> on the course navigation bar.</p>
  <h2>Description</h2>
  ${formatRichTextForHTML(formData.assignmentDescription, '<p>[Please provide a short description of the assignment]</p>')}
  <h3>Rationale</h3>
  ${formatRichTextForHTML(formData.rationale, '<p>This assignment will evaluate the following Course Learning Outcomes:</p>')}
  <ul>
${closHTML || '    <li><strong>CLO1:</strong> [Learning outcome description]</li>'}
  </ul>
  <h2>Directions</h2>
  ${formatRichTextForHTML(formData.directions, '<p>[Provide step-by-step instructions if applicable]</p>')}
  <h2>How Your Assignment Will be Graded</h2>
  ${formatRichTextForHTML(formData.gradingDetails, '<p>A rubric has been created and can be found attached to the submission folder.</p>')}
  <h2>Grading Criteria</h2>
  ${formatRichTextForHTML(formData.gradingCriteria, '<p>(Optional: Describe specific grading criteria here)</p>')}
  <p>See your Program Handbook for the late policy.</p>
  <h3>Tips for Success:</h3>
  ${formatRichTextForHTML(formData.tipsForSuccess, '<p>Tips for completing this assignment successfully will be provided here.</p>')}
${formData.specialInstructions && formData.specialInstructions.trim() !== '' ? `  <h3>Additional Information:</h3>\n  ${formatRichTextForHTML(formData.specialInstructions, '')}` : ''}
${(formData.hyperlinks || []).filter(l => l.label && l.url).length > 0 ? `  <h2>Resources &amp; Links</h2>
  <ul>
${(formData.hyperlinks || []).filter(l => l.label && l.url).map(l => `    <li><a href="${l.url}" target="_blank" rel="noopener noreferrer">${l.label}</a>${l.category !== 'other' ? ` <em>(${HYPERLINK_CATEGORIES.find(c => c.value === l.category)?.label || l.category})</em>` : ''}</li>`).join('\n')}
  </ul>` : ''}
</div>
</div>
</div>`;
        setGeneratedHTML(assignmentHTML);
        setShowHTML(true);
        setShowPrompt(false);
    };

    const generateStudentHTML = () => {
        const clos = formData.clos || [];
        const closHTML = clos.filter(clo => clo.text.trim() !== '').map(clo => `        <li><strong>${clo.type} ${clo.number}:</strong> ${clo.text}</li>`).join('\n');
        const dueText = formData.dueWeek ? `Week ${formData.dueWeek}${formData.dueDateCustom ? ` - ${formData.dueDateCustom}` : ''}` : formData.dueDateCustom || 'See your Instructional Plan for exact due dates';
        const formatRichTextForStudentHTML = (htmlContent, fallbackText) => {
            if (!htmlContent || htmlContent.trim() === '') return `<p>${fallbackText}</p>`;
            return htmlContent.includes('<') && htmlContent.includes('>') ? htmlContent.replace(/<div>/g, '<p>').replace(/<\/div>/g, '</p>').replace(/<br\s*\/?>/g, '<br>').trim() : `<p>${htmlContent}</p>`;
        };
        const assignmentHeaderHTML = generateAssignmentHeader({ title: formData.assignmentTitle || 'Assignment Title', assignmentNumber: formData.assignmentNumber || 'X', courseCode: formData.courseCode || 'Course Code', weight: formData.weightPercentage || 'X' }, { maxHeight: 70, maxWidth: 200 });
        const studentHTML = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8"/>
    <meta content="width=device-width, initial-scale=1" name="viewport"/>
    <title>${formData.assignmentTitle || 'Assignment'} - ${formData.courseCode || 'Course'}</title>
    <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; margin: 0; padding: 20px; background-color: #f8fafc; color: #334155; }
        .container { max-width: 800px; margin: 0 auto; background: white; padding: 30px; border-radius: 10px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); }
        .assignment-header { margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #e2e8f0; }
        h1 { color: #1e40af; margin: 0 0 10px 0; font-size: 2rem; font-weight: 700; }
        h2 { color: #1e40af; border-bottom: 2px solid #3b82f6; padding-bottom: 5px; margin-top: 30px; margin-bottom: 15px; }
        h3 { color: #1f2937; margin-top: 25px; margin-bottom: 10px; }
        .due-info { background: #dbeafe; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #3b82f6; }
        .submission-info { background: #f0fdf4; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #16a34a; }
        ul { padding-left: 20px; }
        li { margin-bottom: 8px; }
        .school-logo, .assignment-logo { max-height: 70px; max-width: 200px; object-fit: contain; }
        footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; color: #64748b; font-size: 0.9rem; }
        @media print {
            body { background: white; }
            .container { box-shadow: none; }
            .school-logo, .assignment-logo { max-height: 50px !important; }
        }
    </style>
</head>
<body>
    <div class="container">
        ${assignmentHeaderHTML}
        <div class="due-info"><strong>📅 Due:</strong> ${dueText}</div>
        <div class="submission-info"><strong>📤 Submit to:</strong> The <strong>Assignment ${formData.submissionFolderNumber || formData.assignmentNumber || 'X'}</strong> submission folder. Submission folders can be accessed by selecting <strong>Course Tools</strong> and then <strong>Assignments</strong> on the course navigation bar.</div>
        <h2>📋 Description</h2>
        ${formatRichTextForStudentHTML(formData.assignmentDescription, 'Please provide a short description of the assignment')}
        <h2>🎯 Rationale</h2>
        ${formatRichTextForStudentHTML(formData.rationale, 'This assignment will evaluate the following Course Learning Outcomes:')}
        <ul>
${closHTML || '            <li><strong>CLO1:</strong> [Learning outcome description]</li>'}
        </ul>
        <h2>📝 Directions</h2>
        ${formatRichTextForStudentHTML(formData.directions, 'Provide step-by-step instructions if applicable')}
        <h2>📊 How Your Assignment Will be Graded</h2>
        ${formatRichTextForStudentHTML(formData.gradingDetails, 'A rubric has been created and can be found attached to the submission folder.')}
        <h2>🏆 Grading Criteria</h2>
        ${formatRichTextForStudentHTML(formData.gradingCriteria, '(Optional: Describe specific grading criteria here)')}
        <p><strong>⚠️ Late Policy:</strong> See your Program Handbook for the late policy.</p>
        <h2>💡 Tips for Success</h2>
        ${formatRichTextForStudentHTML(formData.tipsForSuccess, 'Tips for completing this assignment successfully will be provided here.')}
${formData.specialInstructions && formData.specialInstructions.trim() !== '' ? `        <h2>ℹ️ Additional Information</h2>
        ${formatRichTextForStudentHTML(formData.specialInstructions, '')}
` : ''}${(formData.hyperlinks || []).filter(l => l.label && l.url).length > 0 ? `        <h2>🔗 Resources & Links</h2>
        <ul style="list-style: none; padding-left: 0;">
${(formData.hyperlinks || []).filter(l => l.label && l.url).map(l => `            <li style="margin-bottom: 8px; padding: 8px 12px; background: #f0f9ff; border-radius: 6px; border-left: 3px solid #0ea5e9;"><a href="${l.url}" target="_blank" rel="noopener noreferrer" style="color: #0369a1; text-decoration: none; font-weight: 500;">${l.label}</a>${l.category !== 'other' ? ` <span style="color: #64748b; font-size: 0.85em;">(${HYPERLINK_CATEGORIES.find(c => c.value === l.category)?.label || l.category})</span>` : ''}</li>`).join('\n')}
        </ul>
` : ''}        <footer>
            <p><strong>${formData.courseCode || 'Course Code'}</strong> | ${new Date().getFullYear()}</p>
            <p>Last Updated: ${new Date().toLocaleDateString()}</p>
            ${hasSchoolLogo() ? '<p>Generated with institutional branding</p>' : ''}
        </footer>
    </div>
</body>
</html>`;
        return studentHTML;
    };

    const exportStudentHTML = () => {
        const studentHTML = generateStudentHTML();
        const element = document.createElement('a');
        const file = new Blob([studentHTML], { type: 'text/html' });
        element.href = URL.createObjectURL(file);
        const assignmentNum = formData.assignmentNumber || 'X';
        const courseCode = formData.courseCode || 'Course';
        element.download = `${courseCode}_Assignment_${assignmentNum}_${formData.assignmentTitle.replace(/\s+/g, '_').toLowerCase()}_Student.html`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const exportPrompt = () => {
        const element = document.createElement('a');
        const file = new Blob([generatedPrompt], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        const assignmentNum = formData.assignmentNumber || 'X';
        element.download = `assignment-${assignmentNum}-ai-prompt-${formData.assignmentTitle.replace(/\s+/g, '-').toLowerCase()}.txt`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const exportHTML = () => {
        const element = document.createElement('a');
        const file = new Blob([generatedHTML], { type: 'text/html' });
        element.href = URL.createObjectURL(file);
        const submissionNumber = formData.submissionFolderNumber || formData.assignmentNumber || '1';
        element.download = `Assignment_${submissionNumber}_${formData.assignmentTitle.replace(/\s+/g, '_').toLowerCase()}_D2L.html`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const copyToClipboard = (content) => {
        navigator.clipboard.writeText(content);
        alert('Content copied to clipboard!');
    };

    const clearForm = () => {
        if (window.confirm('Are you sure you want to clear all form data? This action cannot be undone.')) {
            initializeAssignmentPromptFormData();
            setGeneratedPrompt('');
            setGeneratedHTML('');
            setShowPrompt(false);
            setShowHTML(false);
        }
    };

    const isFormValid = formData.assignmentTitle?.trim() && formData.assignmentNumber?.trim() && formData.weightPercentage?.trim() && formData.subjectArea?.trim() && formData.assignmentDescription?.trim() && (formData.clos || []).some(clo => clo.text?.trim());
    const hasDataToSave = formData.assignmentTitle?.trim() || formData.assignmentDescription?.trim() || (formData.clos || []).some(clo => clo.text?.trim());

    if (!formData) return <div>Loading...</div>;

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <FileText className="w-8 h-8 text-orange-600" />
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Assignment Details</h1>
                                <p className="text-gray-600">Define assignment details, criteria, and generate D2L HTML pages</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button onClick={saveAssignmentJSON} disabled={!hasDataToSave} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${hasDataToSave ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} title="Save current assignment as JSON file">
                                <Save className="w-4 h-4" /> Save JSON
                            </button>
                            <button onClick={loadAssignmentJSON} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-all duration-200 shadow-sm" title="Load assignment from JSON file">
                                <Upload className="w-4 h-4" /> Load JSON
                            </button>
                            <button onClick={clearForm} className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-all duration-200 shadow-sm" title="Reset all form fields to default">
                                <RefreshCw className="w-4 h-4" /> Clear Form
                            </button>
                        </div>
                    </div>
                    <input ref={fileInputRef} type="file" accept=".json" onChange={handleFileLoad} className="hidden" />
                    <div className="bg-gradient-to-r from-orange-50 to-green-50 rounded-lg p-6 border border-orange-200">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0"><div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center"><Lightbulb className="w-5 h-5 text-orange-600" /></div></div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-gray-900 mb-2">How It Works</h3>
                                <div className="flex items-center gap-4 text-sm text-gray-700 mb-3">
                                    <div className="flex items-center gap-2"><div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold text-xs">1</div><span>Fill assignment details</span></div>
                                    <ArrowRight className="w-4 h-4 text-gray-400" />
                                    <div className="flex items-center gap-2"><div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold text-xs">2</div><span>Generate AI prompt or D2L HTML</span></div>
                                    <ArrowRight className="w-4 h-4 text-gray-400" />
                                    <div className="flex items-center gap-2"><div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-semibold text-xs">3</div><span>Export or copy content</span></div>
                                </div>
                                <p className="text-sm text-gray-600">The AI prompt helps ChatGPT create assignment pages, while the D2L HTML output can be pasted directly into D2L's content editor.<strong> Save your work as JSON files to easily resume later!</strong></p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-blue-800 mb-4">Basic Assignment Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><label className="block text-sm font-medium text-gray-700 mb-1">Assignment Title *</label><input type="text" value={formData.assignmentTitle || ''} onChange={(e) => handleInputChange('assignmentTitle', e.target.value)} className="w-full p-3 border rounded-lg" placeholder="e.g., Digital Portfolio Project" /></div>
                            <div><label className="block text-sm font-medium text-gray-700 mb-1">Assignment Number *</label><input type="text" value={formData.assignmentNumber || ''} onChange={(e) => handleInputChange('assignmentNumber', e.target.value)} className="w-full p-3 border rounded-lg" placeholder="e.g., 1, 2, 3" /></div>
                            <div><label className="block text-sm font-medium text-gray-700 mb-1">Weight Percentage *</label><input type="text" value={formData.weightPercentage || ''} onChange={(e) => handleInputChange('weightPercentage', e.target.value)} className="w-full p-3 border rounded-lg" placeholder="e.g., 25" /></div>
                            <div><label className="block text-sm font-medium text-gray-700 mb-1">Subject Area *</label><input type="text" value={formData.subjectArea || ''} onChange={(e) => handleInputChange('subjectArea', e.target.value)} className="w-full p-3 border rounded-lg" placeholder="e.g., Computer Graphics, Web Development" /></div>
                            <div><label className="block text-sm font-medium text-gray-700 mb-1">Course Code</label><input type="text" value={formData.courseCode || ''} onChange={(e) => handleInputChange('courseCode', e.target.value)} className="w-full p-3 border rounded-lg" placeholder="e.g., COMP1234" /></div>
                            <div><label className="block text-sm font-medium text-gray-700 mb-1">Submission Folder Number</label><input type="text" value={formData.submissionFolderNumber || ''} onChange={(e) => handleInputChange('submissionFolderNumber', e.target.value)} className="w-full p-3 border rounded-lg" placeholder="Auto-synced with Assignment Number" /><p className="text-sm text-gray-500 mt-1">Automatically syncs with Assignment Number when changed</p></div>
                        </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-yellow-800 mb-4">Due Date Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><label className="block text-sm font-medium text-gray-700 mb-1">Due Week</label><select value={formData.dueWeek || ''} onChange={(e) => handleInputChange('dueWeek', e.target.value)} className="w-full p-3 border rounded-lg">{generateWeekOptions()}</select></div>
                            <div><label className="block text-sm font-medium text-gray-700 mb-1">Custom Due Date</label><input type="date" value={formData.dueDateCustom || ''} onChange={(e) => handleInputChange('dueDateCustom', e.target.value)} className="w-full p-3 border rounded-lg" /></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">If no dates provided, will default to "See your Instructional Plan for exact due dates."</p>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-green-800 mb-4">Assignment Content</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Description *</label>
                                <div className="relative">
                                    <TiptapEditor
                                        value={formData.assignmentDescription}
                                        onChange={(html) => handleInputChange('assignmentDescription', html)}
                                        placeholder="Provide a clear description of what students need to create or submit..."
                                        showToolbar={false}
                                        editorClassName="min-h-[96px]"
                                    />
                                    <button type="button" onClick={() => openInlineEditor(formData.assignmentDescription, 'Assignment Description', (newContent) => handleInputChange('assignmentDescription', newContent))} className="absolute top-2 right-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600"><Maximize2 size={14} /></button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Rationale</label>
                                <div className="relative">
                                    <TiptapEditor
                                        value={formData.rationale}
                                        onChange={(html) => handleInputChange('rationale', html)}
                                        placeholder="This assignment will evaluate the following Course Learning Outcomes:"
                                        showToolbar={false}
                                        editorClassName="min-h-[80px]"
                                    />
                                    <button type="button" onClick={() => openInlineEditor(formData.rationale, 'Rationale', (newContent) => handleInputChange('rationale', newContent))} className="absolute top-2 right-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600"><Maximize2 size={14} /></button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-purple-800">Learning Outcomes</h3>
                            <button type="button" onClick={addCLO} disabled={(formData.clos || []).length >= 15} className={`flex items-center gap-2 px-3 py-1 text-sm rounded-lg font-medium ${(formData.clos || []).length >= 15 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'}`}><Plus className="w-4 h-4" /> Add CLO</button>
                        </div>
                        <div className="space-y-3">
                            {(formData.clos || []).map((clo) => (
                                <div key={clo.id} className="flex items-start gap-3 p-3 bg-white rounded border">
                                    <div className="flex gap-2">
                                        <select value={clo.type} onChange={(e) => updateCLO(clo.id, 'type', e.target.value)} className="border rounded px-2 py-1 text-sm"><option value="CLO">CLO</option><option value="ULO">ULO</option></select>
                                        <select value={clo.number} onChange={(e) => updateCLO(clo.id, 'number', e.target.value)} className="w-16 border rounded px-2 py-1 text-sm">{generateNumberOptions()}</select>
                                    </div>
                                    <input type="text" value={clo.text} onChange={(e) => updateCLO(clo.id, 'text', e.target.value)} className="flex-1 border rounded px-3 py-1" placeholder="Enter learning outcome description..." />
                                    <button type="button" onClick={() => removeCLO(clo.id)} disabled={(formData.clos || []).length <= 1} className={`p-1 rounded ${(formData.clos || []).length <= 1 ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`}><Minus className="w-4 h-4" /></button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-indigo-800 mb-4">Assignment Instructions & Due Date</h3>
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Directions</label>
                                <div className="relative">
                                    <TiptapEditor
                                        value={formData.directions}
                                        onChange={(html) => handleInputChange('directions', html)}
                                        placeholder="Provide step-by-step instructions..."
                                        showToolbar={false}
                                        editorClassName="min-h-[96px]"
                                    />
                                    <button type="button" onClick={() => openInlineEditor(formData.directions, 'Directions', (newContent) => handleInputChange('directions', newContent))} className="absolute top-2 right-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600"><Maximize2 size={14} /></button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">How Your Assignment Will be Graded</label>
                                <div className="relative">
                                    <TiptapEditor
                                        value={formData.gradingDetails}
                                        onChange={(html) => handleInputChange('gradingDetails', html)}
                                        placeholder="Describe how the assignment will be graded..."
                                        showToolbar={false}
                                        editorClassName="min-h-[96px]"
                                    />
                                    <button type="button" onClick={() => openInlineEditor(formData.gradingDetails, 'Grading Details', (newContent) => handleInputChange('gradingDetails', newContent))} className="absolute top-2 right-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600"><Maximize2 size={14} /></button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Grading Criteria (Optional)</label>
                                <div className="relative">
                                    <TiptapEditor
                                        value={formData.gradingCriteria}
                                        onChange={(html) => handleInputChange('gradingCriteria', html)}
                                        placeholder="List specific grading criteria for the AI prompt..."
                                        showToolbar={false}
                                        editorClassName="min-h-[96px]"
                                    />
                                    <button type="button" onClick={() => openInlineEditor(formData.gradingCriteria, 'Grading Criteria', (newContent) => handleInputChange('gradingCriteria', newContent))} className="absolute top-2 right-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600"><Maximize2 size={14} /></button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Tips for Success</label>
                                <div className="relative">
                                    <TiptapEditor
                                        value={formData.tipsForSuccess}
                                        onChange={(html) => handleInputChange('tipsForSuccess', html)}
                                        placeholder="Provide helpful tips and strategies..."
                                        showToolbar={false}
                                        editorClassName="min-h-[96px]"
                                    />
                                    <button type="button" onClick={() => openInlineEditor(formData.tipsForSuccess, 'Tips for Success', (newContent) => handleInputChange('tipsForSuccess', newContent))} className="absolute top-2 right-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600"><Maximize2 size={14} /></button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                                <div className="relative">
                                    <TiptapEditor
                                        value={formData.specialInstructions}
                                        onChange={(html) => handleInputChange('specialInstructions', html)}
                                        placeholder="Any additional notes or special requirements..."
                                        showToolbar={false}
                                        editorClassName="min-h-[96px]"
                                    />
                                    <button type="button" onClick={() => openInlineEditor(formData.specialInstructions, 'Additional Information', (newContent) => handleInputChange('specialInstructions', newContent))} className="absolute top-2 right-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600"><Maximize2 size={14} /></button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Link2 className="w-5 h-5 text-teal-700" />
                                <h3 className="text-lg font-semibold text-teal-800">Resource Links</h3>
                            </div>
                            <button type="button" onClick={addHyperlink} className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg font-medium bg-teal-600 text-white hover:bg-teal-700 transition-colors">
                                <Plus className="w-4 h-4" /> Add Link
                            </button>
                        </div>
                        <p className="text-sm text-teal-700 mb-4">Add links to handouts, D2L pages, reference materials, videos, or any other resources students may need.</p>
                        {(formData.hyperlinks || []).length === 0 ? (
                            <div className="text-center py-8 bg-white rounded-lg border border-dashed border-teal-300">
                                <Link2 className="w-10 h-10 text-teal-300 mx-auto mb-2" />
                                <p className="text-sm text-gray-500">No resource links added yet.</p>
                                <button type="button" onClick={addHyperlink} className="mt-2 text-sm text-teal-600 hover:text-teal-800 font-medium underline">
                                    Add your first link
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {(formData.hyperlinks || []).map((link, index) => (
                                    <div key={link.id} className="bg-white rounded-lg border border-teal-200 p-4 hover:shadow-sm transition-shadow">
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold">{index + 1}</span>
                                            </div>
                                            <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-3">
                                                <div className="md:col-span-3">
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                                                    <select
                                                        value={link.category}
                                                        onChange={(e) => updateHyperlink(link.id, 'category', e.target.value)}
                                                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                                    >
                                                        {HYPERLINK_CATEGORIES.map(cat => (
                                                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="md:col-span-4">
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Display Name</label>
                                                    <input
                                                        type="text"
                                                        value={link.label}
                                                        onChange={(e) => updateHyperlink(link.id, 'label', e.target.value)}
                                                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                                        placeholder="e.g., Assignment Handout PDF"
                                                    />
                                                </div>
                                                <div className="md:col-span-5">
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">URL</label>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="url"
                                                            value={link.url}
                                                            onChange={(e) => updateHyperlink(link.id, 'url', e.target.value)}
                                                            className="flex-1 p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                                            placeholder="https://..."
                                                        />
                                                        {link.url && (
                                                            <a
                                                                href={link.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex-shrink-0 p-2 text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-lg transition-colors"
                                                                title="Open link in new tab"
                                                            >
                                                                <ExternalLink className="w-4 h-4" />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeHyperlink(link.id)}
                                                className="flex-shrink-0 mt-5 p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Remove link"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex gap-4 pt-6 flex-wrap">
                        <button onClick={generateAIPrompt} disabled={!isFormValid} className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium ${isFormValid ? 'bg-orange-600 text-white hover:bg-orange-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}><Sparkles className="w-5 h-5" /> Generate AI Prompt</button>
                        <button onClick={generateAssignmentHTML} disabled={!isFormValid} className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium ${isFormValid ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}><Code className="w-5 h-5" /> Generate D2L HTML</button>
                        <button onClick={exportStudentHTML} disabled={!isFormValid} className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium ${isFormValid ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}><FileText className="w-5 h-5" /> Export Student HTML</button>
                    </div>

                    {!isFormValid && (
                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                            <p className="text-sm text-yellow-700"><strong>⚠️ Required:</strong> Complete assignment title, number, weight, subject area, description, and at least one learning outcome.</p>
                        </div>
                    )}
                </div>

                {(showPrompt || showHTML) && (
                    <div ref={resultsRef} className="mt-8 bg-white rounded-lg shadow-sm border">
                        <div className="p-6 border-b"><h2 className="text-xl font-semibold text-gray-900">{showPrompt ? 'Generated AI Prompt' : 'Generated D2L HTML'}</h2><p className="text-gray-600 mt-1">{showPrompt ? 'Copy this prompt and paste it into ChatGPT to generate your assignment page' : 'Copy this HTML and paste it directly into D2L\'s content editor'}</p></div>
                        <div className="p-6">
                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-96 overflow-y-auto"><pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">{showPrompt ? generatedPrompt : generatedHTML}</pre></div>
                            <div className="flex gap-4 mt-4">
                                {showPrompt && (<><button onClick={exportPrompt} className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"><Download className="w-4 h-4" /> Export Prompt (.txt)</button><button onClick={() => copyToClipboard(generatedPrompt)} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"><FileText className="w-4 h-4" /> Copy to Clipboard</button></>)}
                                {showHTML && (<><button onClick={exportHTML} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"><Download className="w-4 h-4" /> Export D2L HTML</button><button onClick={() => copyToClipboard(generatedHTML)} className="flex items-center gap-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"><Code className="w-4 h-4" /> Copy for D2L</button></>)}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {inlineEditor.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b"><h4 className="text-xl font-semibold text-gray-800">{inlineEditor.field}</h4><div className="flex gap-3"><button onClick={() => closeInlineEditor(true)} className="px-6 py-3 bg-blue-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium"><Save size={18} />Save & Close</button><button onClick={() => closeInlineEditor(false)} className="px-6 py-3 bg-red-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium"><X size={18} />Cancel</button></div></div>
                        <div className="p-6 flex-grow overflow-y-auto"><TiptapEditor ref={editorRef} value={inlineEditor.content} onChange={(html) => { richTextContentRef.current = html; }} placeholder={`Enter details for ${inlineEditor.field}...`} /></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssignmentPromptGenerator;
